import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider, useLang } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Safety from "@/pages/Safety";
import HSE from "@/pages/HSE";
import HseFieldReport from "@/pages/HseFieldReport";
import HR from "@/pages/HR";
import MyWork from "@/pages/MyWork";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Careers from "@/pages/Careers";
import Operations from "@/pages/Operations";
import Bitacora from "@/pages/Bitacora";
import Inventory from "@/pages/Inventory";
import Finance from "@/pages/Finance";
import Maintenance from "@/pages/Maintenance";
import Camp from "@/pages/Camp";
import VerifyPdf from "@/pages/VerifyPdf";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SecurityDashboard from "@/pages/SecurityDashboard";
import AuditHistory from "@/pages/AuditHistory";
import HrValidate from "@/pages/HrValidate";
import KioskSignatures from "@/pages/KioskSignatures";
import KioskMobile from "@/pages/KioskMobile";
import EppKioskSign from "@/pages/EppKioskSign";
import BitacoraVerify from "@/pages/BitacoraVerify";
import ConfirmDeleteRoot from "@/components/ConfirmDeleteDialog";

function Protected({ children, adminOnly = false, perm = null }) {
  const { user } = useAuth();
  const { has } = usePermissions();
  const { t } = useLang();
  if (user === null) return <div className="flex h-screen items-center justify-center text-slate-500">{t("loading")}</div>;
  if (user === false) return <Navigate to="/login" replace />;
  // Force password reset before letting the user access anything.
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (perm && !has(perm)) {
    // Fallback amigable: si el usuario tiene mywork.view lo mandamos a /me
    // (por ejemplo un operador aterrizando en / por error).
    if (perm === "dashboard.view" && has("mywork.view")) {
      return <Navigate to="/me" replace />;
    }
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <p className="font-heading font-black text-2xl text-slate-900">403 · Sin acceso</p>
          <p className="text-sm text-slate-500 mt-2 max-w-md">
            No tienes permiso para ver esta sección. Contacta a un administrador si necesitas acceso.
          </p>
          <p className="text-[10px] text-slate-400 mt-3 font-mono">Permiso requerido: {perm}</p>
        </div>
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/verify" element={<VerifyPdf />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={<ChangePasswordRoute />} />
            <Route path="/hr-validate" element={<HrValidate />} />
            <Route path="/kiosk-signatures" element={<KioskSignatures />} />
            <Route path="/kiosk" element={<Navigate to="/kiosk-signatures" replace />} />
            <Route path="/kiosk-mobile" element={<KioskMobile />} />
            <Route path="/epp-kiosk-sign" element={<EppKioskSign />} />
            <Route path="/verify-bitacora/:token" element={<BitacoraVerify />} />
            <Route path="/" element={<Protected perm="dashboard.view"><Dashboard /></Protected>} />
            <Route path="/me" element={<Protected perm="mywork.view"><MyWork /></Protected>} />
            <Route path="/projects" element={<Protected perm="projects.view"><Projects /></Protected>} />
            <Route path="/projects/:id" element={<Protected perm="projects.view"><ProjectDetail /></Protected>} />
            <Route path="/operations" element={<Protected perm="operations.view"><Operations /></Protected>} />
            <Route path="/bitacora" element={<Protected perm="bitacora.view"><Bitacora /></Protected>} />
            <Route path="/inventory" element={<Protected perm="inventory.view"><Inventory /></Protected>} />
            <Route path="/finance" element={<Protected perm="finance.view"><Finance /></Protected>} />
            <Route path="/finanalysis" element={<Navigate to="/finance" replace />} />
            <Route path="/maintenance" element={<Protected perm="maintenance.view"><Maintenance /></Protected>} />
            <Route path="/camp" element={<Protected perm="camp.view"><Camp /></Protected>} />
            <Route path="/safety" element={<Protected perm="safety.view"><Safety /></Protected>} />
            <Route path="/hse" element={<Protected perm="hse.view"><HSE /></Protected>} />
            <Route path="/hse/field" element={<Protected perm="hse.view"><HseFieldReport /></Protected>} />
            <Route path="/hr" element={<Protected perm="hr.view"><HR /></Protected>} />
            <Route path="/users" element={<Protected adminOnly><Users /></Protected>} />
            <Route path="/settings" element={<Protected adminOnly><Settings /></Protected>} />
            <Route path="/admin/security" element={<Protected adminOnly><SecurityDashboard /></Protected>} />
            <Route path="/admin/audit-history" element={<Protected adminOnly><AuditHistory /></Protected>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <ConfirmDeleteRoot />
      </AuthProvider>
    </LangProvider>
  );
}

/** Route guard for /change-password: only renders when the user is
 * authenticated AND has must_change_password flagged. Redirects otherwise. */
function ChangePasswordRoute() {
  const { user } = useAuth();
  const { t } = useLang();
  if (user === null) return <div className="flex h-screen items-center justify-center text-slate-500">{t("loading")}</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/" replace />;
  return <ChangePassword />;
}

export default App;
